import { useState, useCallback } from 'react';

/**
 * Tipo para errores de API
 */
interface ApiError {
  message?: string;
  error?: string;
  details?: string;
}

/**
 * Hook personalizado para manejo centralizado de errores
 * Estandariza el manejo de errores en todos los componentes modales
 */
export const useErrorHandler = () => {
  const [error, setError] = useState('');

  const clearError = useCallback(() => {
    setError('');
  }, []);

  const handleError = useCallback((message: string) => {
    setError(message);
  }, []);

  const handleApiError = useCallback((error: unknown, defaultMessage: string = 'Ocurrió un error inesperado') => {
    console.error('API Error:', error);
    
    if (error instanceof Error) {
      setError(error.message);
    } else if (typeof error === 'string') {
      setError(error);
    } else if (error && typeof error === 'object' && 'message' in error) {
      setError((error as ApiError).message || defaultMessage);
    } else {
      setError(defaultMessage);
    }
  }, []);

  const handleValidationError = useCallback((condition: boolean, errorMessage: string) => {
    if (condition) {
      setError(errorMessage);
      return true; // Hay error
    }
    return false; // No hay error
  }, []);

  // Limpiar error automáticamente después de 5 segundos
  const autoClearError = useCallback(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return timer;
    }
  }, [error]);

  return {
    error,
    setError,
    clearError,
    handleError,
    handleApiError,
    handleValidationError,
    autoClearError
  };
};
